import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { FormArray, FormControl, FormGroup } from '@angular/forms'
import { Observable, Subject } from 'rxjs'
import { map } from 'rxjs/operators'
import { environment } from '../../../../../../environments/environment'
import { BadgeElement, BadgeElementFieldType, TestBadgeData } from '../models/badge-variable-elements.model'
import { BasicField, FormTab, TemplateLayout, ToolbarAction, VariableElementResponse } from '../types/badge.type'

const BADGE_PADDING_IN_MM = 1
const DROP_AREA_SIZE_IN_MM = 1

export type updateBadgePayload = {
  form_id: number
  badge_front_html_content?: string
  reset?: boolean
  front_fixed_element?: string
  background_preprinted?: boolean
}

export type updateBadgeResponse = {
  badge: BadgeResponse
}

export type BadgeResponse = {
  form_id: number
  badge_front_html_content: string
  background_preprinted: boolean
  front_fixed_element: string
}

type BadgeBackgroundUpload = {
  front_fixed_element?: string
  back_fixed_element?: string
} | null

@Injectable()
export class BadgeEditorService {
  public personaFields: BasicField[] = []
  public isDraggingStarted$ = new Subject<BadgeElement>()
  public isDragging$ = new Subject<boolean>()
  public toolbarAction$ = new Subject<ToolbarAction>()
  public hasDropped$ = new Subject<BadgeElement>()
  public selectedTemplate?: TemplateLayout
  public badgeHeightInPX!: number
  public badgeWidthInPX!: number
  public badge!: BadgeResponse
  public isDTCM: boolean = false
  public frontFixedElement?: string
  public backFixedElement?: string
  public badgeBackgroundUploadChanges$ = new Subject<BadgeBackgroundUpload>()

  constructor(private _http: HttpClient) {}

  public getBadge(formId: number) {
    return this._http.get<updateBadgeResponse>(`${environment.planningApi}badges/${formId}`)
  }
  public updateBadge(badge: updateBadgePayload) {
    return this._http.patch<updateBadgeResponse>(`${environment.planningApi}badges/update`, badge)
  }

  public resetDefaultBadge(badge: updateBadgePayload) {
    badge.reset = true
    return this._http.patch<updateBadgeResponse>(`${environment.planningApi}badges/update`, badge)
  }

  /**
   * @method saveBadge
   * @description Save badge API
   * @param payload accepts BadgeResponse type
   * @param eventId accepts number
   * @param personaId accepts number
   * @returns Observable<Response<BadgeResponse>>
   */
  public saveBadge(payload: updateBadgePayload, template: string = ''): Observable<updateBadgeResponse> {
    // payload = template ? {...payload, template} : {...payload};
    payload.badge_front_html_content = this.clearBadgeExtraHTML(payload.badge_front_html_content, payload?.background_preprinted, payload?.front_fixed_element)
    return this._http
      .patch<updateBadgeResponse>(`${environment.planningApi}badges/update`, payload)

      .pipe(
        map((response) => {
          this.badge = response.badge
          response.badge.badge_front_html_content = this._prepareTheBadge(response.badge.badge_front_html_content)
          return this._prepareTheBadgeToEdit(response)
        })
      )
  }

  /**
   * @description Prepare The Badge for Editing
   * @param html_content string
   */
  private _prepareTheBadge(html_content: string): string {
    const parser = new DOMParser()
    const badgeDocument = parser.parseFromString(html_content, 'text/html')
    const badge = badgeDocument.getElementById('badge') as HTMLElement
    return badge.outerHTML
  }

  /**
   * @method clearBadgeExtraHTML
   * @description Clear Extra HTML elements from badge
   * @param badgeTemplate accepts BadgeTemplateResponse
   * @returns void
   */
  public clearBadgeExtraHTML(badgeText: string, backgroundPreprinted: boolean, fixed_element: string | null = null): string {
    const parser = new DOMParser()
    const badgeHTML = parser.parseFromString(badgeText, 'text/html')
    const badge = badgeHTML.getElementById('badge') as HTMLElement
    if (!badge) return ''
    const badgeHeight = badge.getAttribute('data-height')
    const badgeWidth = badge.getAttribute('data-width')
    badge.style.height = `${badgeHeight}mm`
    badge.style.width = `${badgeWidth}mm`
    badge.style.position = 'relative'
    const badgeContentContainer = badgeHTML.getElementById('badge-content-container') as HTMLElement
    if (badgeContentContainer) {
      badgeContentContainer.style.zIndex = '2'
      badgeContentContainer.style.gap = '1mm'
      badgeContentContainer.style.paddingLeft = '2mm'
      badgeContentContainer.style.paddingRight = '2mm'
      // Convert px to mm in rows
      if (badgeContentContainer.children?.length) {
        Object.values(badgeContentContainer.children).forEach((row) => ((row as HTMLElement).style.gap = '2mm'))
        badge.removeAttribute('is-badge-empty')
      } else badge.setAttribute('is-badge-empty', '')
    } else badge.setAttribute('is-badge-empty', '')
    // Remove badgeHeightElements
    const columns = badge.querySelectorAll('.draggable-column')
    columns.forEach((col) => {
      const badgeHeightElement = col.querySelector('.height-element') as HTMLElement
      if (!badgeHeightElement) return
      const height = `${badgeHeightElement?.innerText}mm`
      ;(col as HTMLElement).style.width = '100%'
      ;(col as HTMLElement).style.height = height
      badgeHeightElement.remove()
    })
    // Prepare the badge to save it
    const fixedElementsImage = this._createFixedElementsImage()
    if (fixed_element && !backgroundPreprinted) {
      fixedElementsImage.src = fixed_element
      badge.style.backgroundImage = ''
      badge.appendChild(fixedElementsImage)
      badge.removeAttribute('is-badge-empty')
    } else {
      if (!badgeContentContainer?.children?.length) badge.setAttribute('is-badge-empty', '')
      badge.querySelector('.fixed-elements-image')?.remove()
    }

    // Remove frontTemporaryElement
    const temporaryElement = badge.querySelectorAll('.temporary-element')
    temporaryElement.forEach((component) => component.remove())
    // Refactor qr codes style before saving
    const qrCodes = badge.querySelectorAll('.qr-number')
    qrCodes?.forEach((p) => {
      ;(p as HTMLParagraphElement).style.letterSpacing = '0px'
      ;(p as HTMLParagraphElement).style.marginRight = '0px'
    })
    // Refactor upload photo style before saving
    const uploadPhotoElements = badge.querySelectorAll(`[data-element="${BadgeElementFieldType.UPLOAD_PHOTO}"]`)
    uploadPhotoElements?.forEach((element) => {
      const img = document.createElement('img') as HTMLImageElement
      img.src = 'https://library.micetribe.com/images/gallery-img.svg'
      img.setAttribute('data-element', BadgeElementFieldType.UPLOAD_PHOTO)
      img.setAttribute('data-elementid', (element as HTMLElement).getAttribute('data-elementid') as string)
      img.style.height = '100%'
      img.style.border = '1px solid #E5E7EB'
      img.style.borderRadius = (element as HTMLElement).style.borderRadius
      img.style.maxWidth = (element as HTMLElement).style.maxWidth
      img.style.aspectRatio = (element as HTMLElement).style.aspectRatio
      element.parentElement?.prepend(img)
      element.remove()
    })
    badge.style.backgroundImage = ''
    const body = document.querySelector('body')
    badge.style.zIndex = '-999'
    body?.appendChild(badge)
    const appendedBadge = document.querySelector('body > #badge') as HTMLDivElement
    const rows = appendedBadge.querySelector('#badge-content-container')?.children || []
    Object.values(rows).forEach((element, key) => {
      this.resizeTextValueToFitParent(key, element as HTMLDivElement)
    })
    appendedBadge.remove()
    badge.style.zIndex = ''
    return badge.outerHTML
  }

  /**
   * @description Creates the fixed elements html image
   * @returns HTMLImageElement
   */
  private _createFixedElementsImage(): HTMLImageElement {
    const img = document.createElement('img')
    img.classList.add('fixed-elements-image')
    img.style.position = 'absolute'
    img.style.width = '100%'
    img.style.height = '100%'
    img.style.top = '0'
    img.style.left = '0'
    img.style.zIndex = '1'
    return img
  }

  /**
   * @method resizeTextValueToFitParent
   * @description resize text value of the columns to fit parent element
   * @param rowId accepts string
   * @returns void
   */
  public resizeTextValueToFitParent(rowIndex: number, containerHTML?: HTMLDivElement): void {
    const container = containerHTML || (document.querySelector(`#row-${rowIndex + 1}`) as HTMLDivElement)
    if (!container) return
    const rowText = container.querySelectorAll('p')
    if (!rowText) return
    Object.values(rowText).forEach((text) => {
      if (!text || !text.textContent) return
      // Keep QR number size unchanged when it is hidden.
      if (text.classList.contains('qr-number') && (text.parentElement as HTMLElement)?.style.display === 'none') return
      text.style.fontSize = '1px'
      const parentWidth =
        text.classList.contains('qr-number') || containerHTML ? (text?.parentElement?.clientWidth as number) : parseFloat(text?.parentElement?.style.width as string)
      const parentHeight =
        text.classList.contains('qr-number') || containerHTML ? (text?.parentElement?.clientHeight as number) : parseFloat(text?.parentElement?.style.height as string)
      const widthRatio = parentWidth / text.clientWidth
      const heightRatio = parentHeight / text.clientHeight
      const ratio = Math.min(widthRatio, heightRatio)
      const textFontSize = parseInt(text.style.fontSize)
      text.style.fontSize = textFontSize * ratio + 'px'
    })
  }

  /**
   * @method scaleElementToFitContainer
   * @description Calculate scale factor, to scale the badge and fit its container
   * @param element accepts HTMLElement
   * @param container accepts HTMLElement
   * @param extraSpace accepts number
   * @returns void
   */
  public scaleElementToFitContainer(element: HTMLElement, container: HTMLElement, extraSpace: number = 0): void {
    const scaleWidth = (container.clientWidth - extraSpace) / element.offsetWidth
    const scaleHeight = (container.clientHeight - extraSpace) / element.offsetHeight
    element.style.scale = Math.min(scaleWidth, scaleHeight).toString()
  }

  /**
   * @method getElementHeight
   * @description get HTML element height
   * @param selector accepts string
   * @returns number
   */
  public getElementHeight(selector: string, element?: HTMLElement): number {
    return (document.querySelector(selector) || element)?.getBoundingClientRect().height as number
  }

  /**
   * @method convertMMToPX
   * @description Convert  MilliMeter To PX
   * @returns number
   */
  public convertMMToPX(numberInMM: number): number {
    return (numberInMM * (this.badgeHeightInPX || this.getElementHeight('#badge'))) / this.selectedTemplate!.height
  }

  /**
   * @method initializeBadgeDimensions
   * @description initiate badge dimensions
   * @returns void
   */
  public initializeBadgeDimensions(): void {
    this.badgeHeightInPX = this.getElementHeight('#badge') - this.convertMMToPX(BADGE_PADDING_IN_MM)
    this.badgeWidthInPX = document.getElementById('badge')?.getBoundingClientRect().width as number
  }

  /**
   * @method getColumnsCountInRow
   * @description get columns count inside a row
   * @param rowIndex accepts number
   * @returns void
   */
  public getColumnsCountInRow(rowIndex: number, fields: BadgeElement[][]): number {
    return fields[rowIndex]?.filter((field, index) => fields[rowIndex]?.[index - 1]?.columnContainerIndex !== field.columnContainerIndex).length || 0
  }

  /**
   * @method containerHasEnoughSpace
   * @description desc
   * @param container accepts HTMLElement
   * @param defaultHeight accepts number
   * @returns boolean
   */
  public containerHasEnoughSpace(container: HTMLElement, defaultHeight: number): boolean {
    let elementsHeight = 0
    const containerRect = container.getBoundingClientRect()
    const columns = container.querySelectorAll('.draggable-column')
    columns.forEach((col) => {
      elementsHeight += col.getBoundingClientRect().height
    })
    elementsHeight += this.convertMMToPX(DROP_AREA_SIZE_IN_MM) * (columns.length - 1)
    return containerRect.height - elementsHeight - this.convertMMToPX(DROP_AREA_SIZE_IN_MM) * 2 > defaultHeight
  }

  /**
   * @description Prepare The Badge for Editing
   * @param response Response<BadgeResponse>
   * @returns Response<BadgeResponse>
   */
  private _prepareTheBadgeToEdit(response: updateBadgeResponse): updateBadgeResponse {
    const parserFront = new DOMParser()
    const frontBadgeDocument = parserFront.parseFromString(response.badge.badge_front_html_content, 'text/html')
    const frontBadge = frontBadgeDocument.getElementById('badge') as HTMLElement
    frontBadge.style.zIndex = 'unset'
    frontBadge.style.position = 'static'
    this.frontFixedElement = response.badge.front_fixed_element
    response.badge.badge_front_html_content = frontBadge.outerHTML
    return response
  }

  /**
   * @description Clear the badge from the deleted elements
   * @param htmlContent string
   * @param steps FormTab[]
   * @returns string
   */
  public clearBadgeFromDeletedElements(htmlContent: string, steps: FormTab[]): string {
    if (!htmlContent) return htmlContent
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    const badge = doc.querySelector('#badge')
    if (!badge) return htmlContent

    // Find all elements with data-elementid
    const elements = doc.querySelectorAll('[data-elementid]')
    elements.forEach((element) => {
      const elementId = element.getAttribute('data-elementid')
      const elementExists = steps.some((object: FormTab) => object.fields.some((fieldRow: BasicField[]) => fieldRow.some((field: BasicField) => field.id === elementId)))
      if (!elementExists) {
        // Get the column containing this element
        const column = element.closest('.draggable-column')
        if (column) {
          // Get the container of columns
          const container = column.closest('.columns-container')
          if (container) {
            column.remove()
            // If container has no more columns, remove it
            if (!container.querySelector('.draggable-column')) {
              // Get the row containing this container
              const row = container.closest('[id^="row-"]')
              if (row) {
                container.remove()
                // If row has no more containers, remove it
                if (!row.querySelector('.columns-container')) {
                  row.remove()
                }
              }
            }
          }
        }
      }
    })
    // Reindex remaining rows and columns
    let rowIndex = 1
    doc.querySelectorAll('[id^="row-"]').forEach((row) => {
      row.id = `row-${rowIndex}`
      let containerIndex = 1
      row.querySelectorAll('.columns-container').forEach((container) => {
        container.id = `container-${rowIndex}-${containerIndex}`
        let columnIndex = 1
        container.querySelectorAll('.draggable-column').forEach((column) => {
          column.id = `col-${rowIndex}-${columnIndex}`
          columnIndex++
        })
        containerIndex++
      })
      rowIndex++
    })
    return badge.outerHTML
  }

  /**
   * @description Searches for a control in a form group or form array
   * @param formGroup FormGroup | FormArray - the form group or form array to search in
   * @param formControlName string - the name of the control to search for
   * @returns FormControl | null - the control if found, null otherwise
   */
  public searchControl(formGroup: FormGroup | FormArray, formControlName: string): FormControl | null {
    let keys = Object.keys(formGroup.controls)
    for (let key of keys) {
      let control = formGroup.get(key)

      if (control instanceof FormGroup || control instanceof FormArray) {
        let result = this.searchControl(control, formControlName)
        if (result) return result
      } else if (control instanceof FormControl && key === formControlName) return control
    }
    return null
  }

  /**
   * @method createVariableElements
   * @description Creates the variable elements for the badge template from the variable elements model and the response
   * @param variableElementsResponse VariableElementResponse[]
   * @param newValue string
   * @returns BadgeElement[]
   */
  public createVariableElements(variableElementsResponse: VariableElementResponse[], personaName?: string): BadgeElement[] {
    return variableElementsResponse
      .sort((a, b) => Number(a.weight) - Number(b.weight))
      .map((variableElement) => {
        const value =
          variableElement.field_type === 'qr_code' || variableElement.field_type === 'dtcm_qr_code'
            ? TestBadgeData.qr_code
            : variableElement.field_type === 'spacer'
            ? ''
            : variableElement.field_type === 'persona_name'
            ? personaName
            : variableElement.field_type === 'parent_persona_name'
            ? `Parent: ${personaName}`
            : variableElement.value || variableElement.label
        return {
          value,
          label: variableElement.label,
          id: crypto.randomUUID(),
          personaFieldId: variableElement?.id,
          field_type: variableElement.field_type,
          icon: ''
        } as BadgeElement
      })
  }

  /**
   * @TODO: This method is not used anywhere, will be used in the future for adding default badge image
   * @description Create a badge background by drawing inside canvas using event banner image
   * @param imageFile accepts File
   * @returns Observable<File>
   */
  public createBadgeBackground(imageFile: File): Observable<File> {
    return new Observable((observer) => {
      const canvas: HTMLCanvasElement = document.createElement('canvas')
      // Draw image with aspect ratio 82/127
      const aspectRatio = 82 / 127
      // Add initial value for width and height of the canvas
      canvas.width = 820
      canvas.height = canvas.width / aspectRatio

      const reader = new FileReader()
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const img = new Image()
        img.onload = () =>
          this._drawImageOnCanvas(img, canvas).subscribe({
            next: (file) => {
              observer.next(file)
              observer.complete()
            }
          })
        img.src = e.target?.result as string
      }
      reader.readAsDataURL(imageFile)
    })
  }

  /**
   * @TODO: This method is not used anywhere, will be used in the future for adding default badge image
   * @description Draw image in canvas with aspect ratio 80/127
   * @param image accepts HTMLImageElement
   * @param canvas accepts HTMLCanvasElement
   * @returns Observable<File>
   */
  private _drawImageOnCanvas(image: HTMLImageElement, canvas: HTMLCanvasElement): Observable<File> {
    return new Observable((observer) => {
      const canvasWidth = canvas.width
      const canvasHeight = canvas.height

      this._clearCanvas(canvas)

      // Calculate the aspect ratio
      const imageAspectRatio = image.width / image.height
      const canvasAspectRatio = canvasWidth / canvasHeight

      let drawWidth, drawHeight
      if (imageAspectRatio > canvasAspectRatio) {
        drawWidth = canvasWidth
        drawHeight = canvasWidth / imageAspectRatio
      } else {
        drawHeight = canvasHeight
        drawWidth = canvasHeight * imageAspectRatio
      }

      // Draw the image on the canvas, centered horizontally at the top
      const xOffset = (canvasWidth - drawWidth) / 2
      canvas.getContext('2d').drawImage(image, xOffset, 0, drawWidth, drawHeight)

      canvas.toBlob(
        (blob) => {
          if (!blob) return
          const file = new File([blob], 'processed-image.jpg', { type: 'image/jpeg' })
          observer.next(file)
          observer.complete()
        },
        'image/jpeg',
        1
      )
    })
  }

  /**
   * @method _clearCanvas
   * @description Clear the canvas and fill the background with white color
   * @param canvas accepts HTMLCanvasElement
   * @param context accepts CanvasRenderingContext2D
   * @returns void
   */
  private _clearCanvas(canvas: HTMLCanvasElement): void {
    const canvasWidth = canvas.width
    const canvasHeight = canvas.height
    canvas.getContext('2d').clearRect(0, 0, canvasWidth, canvasHeight)
    canvas.getContext('2d').fillStyle = 'white'
    canvas.getContext('2d').fillRect(0, 0, canvasWidth, canvasHeight)
  }

  /**
   * @method createBadgeBackgroundFromUrl
   * @description Create a badge background from a URL and return it as a File
   * @param imageUrl accepts string - URL of the image
   * @returns Observable<File>
   */
  public createBadgeBackgroundFromUrl(imageUrl: string): Observable<File> {
    return new Observable((observer) => {
      const img = new Image()
      img.crossOrigin = 'anonymous' // Enable CORS for external images

      img.onload = () => {
        const canvas: HTMLCanvasElement = document.createElement('canvas')
        const aspectRatio = 82 / 127
        canvas.width = 820
        canvas.height = canvas.width / aspectRatio

        const context = canvas.getContext('2d') as CanvasRenderingContext2D

        this._drawImageOnCanvas(img, canvas).subscribe({
          next: (file) => {
            observer.next(file)
            observer.complete()
          },
          error: (error) => {
            observer.error(error)
          }
        })
      }

      img.onerror = () => {
        observer.error(new Error('Failed to load image from URL'))
      }

      img.src = imageUrl
    })
  }
}
